import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, AreaSeries } from 'lightweight-charts';
import axios from 'axios';
import styles from './TVChart.module.css';

const CHART_HEIGHT = 240;

export default function TVChart({ symbol, theme = 'light' }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const candlesRef   = useRef(null);

  const [chartType, setChartType] = useState('area'); // 'area' | 'candlestick'
  const [error,     setError]     = useState('');

  // ── Build / destroy chart when theme or type changes ─────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const isDark = theme === 'dark';

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: CHART_HEIGHT,
      layout: {
        background: { color: 'transparent' },
        textColor:  isDark ? '#6b8f72' : '#3d6147',
      },
      grid: {
        vertLines: { color: isDark ? 'rgba(74,222,128,0.05)' : 'rgba(26,122,64,0.06)' },
        horzLines: { color: isDark ? 'rgba(74,222,128,0.05)' : 'rgba(26,122,64,0.06)' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: isDark ? '#1a2a1e' : '#d4dece' },
      timeScale: {
        borderColor:    isDark ? '#1a2a1e' : '#d4dece',
        timeVisible:    true,
        secondsVisible: false,
      },
      handleScroll: false,
      handleScale:  false,
    });

    let series;
    if (chartType === 'area') {
      series = chart.addSeries(AreaSeries, {
        lineColor:    isDark ? '#4ade80' : '#1a7a40',
        topColor:     isDark ? 'rgba(74,222,128,0.25)' : 'rgba(26,122,64,0.18)',
        bottomColor:  isDark ? 'rgba(74,222,128,0.02)' : 'rgba(26,122,64,0.02)',
        lineWidth: 2,
      });
    } else {
      series = chart.addSeries(CandlestickSeries, {
        upColor:         isDark ? '#4ade80' : '#1a7a40',
        downColor:       isDark ? '#f87171' : '#c0392b',
        borderUpColor:   isDark ? '#4ade80' : '#1a7a40',
        borderDownColor: isDark ? '#f87171' : '#c0392b',
        wickUpColor:     isDark ? '#4ade80' : '#1a7a40',
        wickDownColor:   isDark ? '#f87171' : '#c0392b',
      });
    }

    chartRef.current  = chart;
    seriesRef.current = series;

    if (candlesRef.current?.length) {
      const data = chartType === 'area'
        ? candlesRef.current.map(c => ({ time: c.time, value: c.close }))
        : candlesRef.current;
      series.setData(data);
      chart.timeScale().fitContent();
    }

    const ro = new ResizeObserver(entries => {
      if (entries[0]) chart.applyOptions({ width: entries[0].contentRect.width });
    });
    ro.observe(containerRef.current);

    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, [theme, chartType]);

  // ── Fetch data when symbol changes ───────────────────────────────────
  useEffect(() => {
    if (!symbol || !seriesRef.current) return;
    setError('');

    axios.get(`/api/chart/${symbol}`)
      .then(({ data }) => {
        if (!seriesRef.current) return;
        if (!data.candles?.length) { setError('Chart data unavailable'); return; }
        candlesRef.current = data.candles;
        const chartData = chartType === 'area'
          ? data.candles.map(c => ({ time: c.time, value: c.close }))
          : data.candles;
        seriesRef.current.setData(chartData);
        chartRef.current?.timeScale().fitContent();
      })
      .catch(() => setError('Chart data unavailable'));
  }, [symbol]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return null;

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <button
          className={`${styles.typeBtn} ${chartType === 'area' ? styles.active : ''}`}
          onClick={() => setChartType('area')}
        >Area</button>
        <button
          className={`${styles.typeBtn} ${chartType === 'candlestick' ? styles.active : ''}`}
          onClick={() => setChartType('candlestick')}
        >Candles</button>
      </div>
      <div ref={containerRef} style={{ width: '100%', height: CHART_HEIGHT }} />
    </div>
  );
}
